import * as React from 'react';

import { TimerIconState } from '~shared/utils/timerIconState';

import { SVGProps } from '../../types';

const COLORS = {
  'grey-600': '#ABB4BD',
  'grey-900': '#5C6975',
  'blue-900': '#273B4C',
  'amber-700': '#E39100',
  'red-800': '#D60000',
  'red-600': '#EB5F5F',
};

type IconTimerSvgProps = SVGProps & {
  state: TimerIconState;
};

export const IconTimerSvg = ({
  title,
  titleId,
  state,
  ...props
}: IconTimerSvgProps) => {
  const getColor = (): string => {
    switch (state) {
      case 'active':
        return COLORS['blue-900'];
      case 'warning':
        return COLORS['amber-700'];
      case 'critical':
        return COLORS['red-800'];
      default:
        return COLORS['grey-600'];
    }
  };

  const color = getColor();

  return (
    <svg
      width={36}
      height={36}
      viewBox="0 0 36 36"
      fill="none"
      focusable="false"
      aria-labelledby={titleId}
      {...props}
    >
      {title ? <title id={titleId}>{title}</title> : null}
      <rect width="36" height="36" rx="4" fill="white" />

      {/* Clock circle */}
      <circle
        cx="18"
        cy="19"
        r="9"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Top line */}
      <line
        x1="15.75"
        y1="7.25"
        x2="20.25"
        y2="7.25"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Top right line */}
      <line
        x1="25.4239"
        y1="9.80762"
        x2="27.1917"
        y2="11.5754"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* State-specific indicators */}
      {state === 'not-started' && (
        <>
          {/* Clock hand */}
          <path
            d="M13.8656 15.6589C13.6903 15.4334 13.7102 15.1126 13.9122 14.9107C14.1142 14.7087 14.435 14.6887 14.6605 14.8641L19.4457 18.5859C20.0744 19.0749 20.1323 20.0043 19.5691 20.5675C19.0059 21.1307 18.0765 21.0728 17.5875 20.4441L13.8656 15.6589Z"
            fill={color}
          />
          {/* Dash badge */}
          <rect x="18" y="15" width="18" height="18" rx="9" fill="white" />
          <line
            x1="29.25"
            y1="24"
            x2="24.75"
            y2="24"
            stroke={COLORS['grey-900']}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )}

      {(state === 'paused' || state === 'ended-early' || state === 'hidden') && (
        /* Plain grey clock hand, no badge */
        <path
          d="M13.8656 15.6589C13.6903 15.4334 13.7102 15.1126 13.9122 14.9107C14.1142 14.7087 14.435 14.6887 14.6605 14.8641L19.4457 18.5859C20.0744 19.0749 20.1323 20.0043 19.5691 20.5675C19.0059 21.1307 18.0765 21.0728 17.5875 20.4441L13.8656 15.6589Z"
          fill={color}
        />
      )}

      {state === 'active' && (
        <path
          d="M13.8656 15.6589C13.6903 15.4334 13.7102 15.1126 13.9122 14.9107C14.1142 14.7087 14.435 14.6887 14.6605 14.8641L19.4457 18.5859C20.0744 19.0749 20.1323 20.0043 19.5691 20.5675C19.0059 21.1307 18.0765 21.0728 17.5875 20.4441L13.8656 15.6589Z"
          fill={color}
        />
      )}

      {state === 'warning' && (
        <path
          d="M11.3231 18.0033C11.7567 15.0732 14.0732 12.7567 17.0033 12.3231C17.5497 12.2422 18 12.6967 18 13.249V18.7518C18 18.8899 17.8881 19 17.75 19L12.25 19C11.6977 19 11.2422 18.5497 11.3231 18.0033Z"
          fill={color}
        />
      )}

      {state === 'critical' && (
        <path
          d="M13.9831 13.5744C14.8529 12.9295 15.8831 12.4888 17.003 12.323C17.5494 12.2422 17.9995 12.6977 17.9995 13.25L17.9995 18.3964C17.9995 18.6192 17.7302 18.7307 17.5728 18.5732L13.9337 14.9341C13.5431 14.5436 13.5395 13.9034 13.9831 13.5744Z"
          fill={color}
        />
      )}

      {state === 'expired' && (
        <>
          {/* Strikethrough line */}
          <line
            x1="9.06457"
            y1="10.0015"
            x2="27.0039"
            y2="27.9408"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          {/* Exclamation mark background */}
          <rect x="18" y="15" width="18" height="18" rx="9" fill="white" />
          {/* Exclamation mark */}
          <path
            d="M27 27C27.8284 27 28.5 27.6716 28.5 28.5C28.5 29.3284 27.8284 30 27 30C26.1716 30 25.5 29.3284 25.5 28.5C25.5 27.6716 26.1716 27 27 27ZM27.9629 18C28.2529 18 28.4823 18.2459 28.4619 18.5352L28.0664 24.0713C28.029 24.5944 27.5938 24.9998 27.0693 25H26.9307C26.4062 24.9998 25.971 24.5944 25.9336 24.0713L25.5381 18.5352C25.5177 18.2459 25.7471 18 26.0371 18H27.9629Z"
            fill={COLORS['red-600']}
          />
        </>
      )}

      {state === 'completed' && (
        <>
          {/* Clock hand */}
          <path
            d="M13.8656 15.6589C13.6903 15.4334 13.7102 15.1126 13.9122 14.9107C14.1142 14.7087 14.435 14.6887 14.6605 14.8641L19.4457 18.5859C20.0744 19.0749 20.1323 20.0043 19.5691 20.5675C19.0059 21.1307 18.0765 21.0728 17.5875 20.4441L13.8656 15.6589Z"
            fill={color}
          />
          {/* Checkmark badge background */}
          <rect x="18" y="15" width="18" height="18" rx="9" fill="white" />
          {/* Checkmark: Figma M4 8.21 L7.87 12 L14 6 offset by +18,+15 */}
          <path
            d="M22 23.21053L25.87097 27L32 21"
            stroke={COLORS['grey-900']}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )}
    </svg>
  );
};
